import type { UnlinkTeacherBody } from '@innovamat/glow-api-client';
import {
  useClassroomsQuery,
  useUnlinkTeacherMutation,
} from '@innovamat/glow-api-client';
import { useQueryClient } from '@tanstack/react-query';
import { getClassroomTeachersQueryKey } from '../hooks/use-classroom-teachers';
import { TeacherBase } from '../modules/teacher';

type ErrorUnlinkTeacher = {
  error: unknown;
  body: {
    classroomId: string;
    teacherId: string;
  };
};

type Props = {
  isPlatformEnabled: boolean;
  organizationId: string;
  onSuccess?: (body: UnlinkTeacherBody) => void;
  onError?: (error: ErrorUnlinkTeacher) => void;
};

type UnlinkTeacher = {
  classroomId: string;
  teacher: TeacherBase;
};

type UseUnlinkTeacher = (props: Props) => {
  isPending: boolean;
  handleUnlinkTeacher: ({ classroomId, teacher }: UnlinkTeacher) => void;
};

const useUnlinkTeacher: UseUnlinkTeacher = ({
  isPlatformEnabled,
  organizationId,
  onSuccess,
  onError,
}: Props) => {
  const queryClient = useQueryClient();

  const { mutate, isPending } = useUnlinkTeacherMutation();

  const handleUnlinkTeacher = ({
    classroomId,
    teacher,
  }: UnlinkTeacher): void => {
    mutate(
      {
        body: {
          classroomId,
          teacherId: teacher.id,
        },
      },
      {
        onSuccess: (_, { body }) => {
          const teachersQueryKey = getClassroomTeachersQueryKey({
            classroomId: classroomId ?? '',
            organizationId,
            isPlatformEnabled,
          });

          const classroomsMineQueryKey = useClassroomsQuery.getKey({
            mine: true,
            orgId: organizationId,
          });

          const classroomsNotMineQueryKey = useClassroomsQuery.getKey({
            mine: false,
            orgId: organizationId,
          });

          queryClient.refetchQueries({ queryKey: teachersQueryKey });
          queryClient.invalidateQueries({ queryKey: classroomsMineQueryKey });
          queryClient.invalidateQueries({
            queryKey: classroomsNotMineQueryKey,
          });

          onSuccess?.(body!);
        },
        onError: (error) => {
          onError?.({ error, body: { classroomId, teacherId: teacher.id } });
        },
      }
    );
  };

  return { isPending, handleUnlinkTeacher };
};

export { useUnlinkTeacher };
export type { ErrorUnlinkTeacher, UseUnlinkTeacher };
